/**
 * Central code-based routing config. Replaces file-based routes/.
 *
 * Pattern:
 *   - Root route (rootRoute) with Suspense + 404 + error UI.
 *   - Pathless layout routes (id: "_auth", "_core", "_config", "_admin", "_debug")
 *     attach auth guards via `beforeLoad`.
 *   - Leaf routes lazy-load their page component from features/<owner>/pages/<page>.
 *
 * To add a route: add a lazy page constant, create a `createRoute({ ... })`
 * entry, append it to the right parent's `addChildren([...])` list.
 */
import {
	type AnyRoute,
	createRootRoute,
	createRoute,
	createRouter,
	redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import {
	isPlausibleSessionToken,
	isSafeRedirectTarget,
} from "@/shared/lib/auth-redirect";
import { APP_ROUTES } from "@/shared/lib/routes/routes/app-routes";
import {
	AcceptInvitationRoute,
	LayoutOutlet,
	NotFound,
	RootError,
	RootLayout,
} from "./router-components";

// --- Page components -------------------------------------------------------

const AccountPage = lazy(() =>
	import("@/pages/account/index").then((module) => ({
		default: module.AccountPage,
	})),
);
const AdminPage = lazy(() =>
	import("@/pages/superuser/index").then((module) => ({
		default: module.AdminPage,
	})),
);
const RolesPage = lazy(() =>
	import("@/pages/roles/index").then((module) => ({
		default: module.RolesPage,
	})),
);
const ConfigPage = lazy(() =>
	import("@/pages/config/index").then((module) => ({
		default: module.ConfigPage,
	})),
);
const LoginPage = lazy(() =>
	import("@/pages/login/index").then((module) => ({
		default: module.LoginPage,
	})),
);
const PendingOrgPage = lazy(() =>
	import("@/pages/pending-org/index").then((module) => ({
		default: module.PendingOrgPage,
	})),
);
const BillingPage = lazy(() =>
	import("@/pages/billing/index").then((module) => ({
		default: module.BillingPage,
	})),
);
const CampaignsPage = lazy(() =>
	import("@/pages/campaigns/index").then((module) => ({
		default: module.CampaignsPage,
	})),
);
const CampaignAuthorPage = lazy(() =>
	import("@/pages/campaigns/index").then((module) => ({
		default: module.CampaignAuthorPage,
	})),
);
const CampaignDetailPage = lazy(() =>
	import("@/pages/campaigns/index").then((module) => ({
		default: module.CampaignDetailPage,
	})),
);
const OnboardingCheckoutPage = lazy(() =>
	import("@/pages/onboarding-checkout/index").then((module) => ({
		default: module.OnboardingCheckoutPage,
	})),
);
const OnboardingSuccessPage = lazy(() =>
	import("@/pages/onboarding-success/index").then((module) => ({
		default: module.OnboardingSuccessPage,
	})),
);
const ContactsPage = lazy(() =>
	import("@/pages/contacts/index").then((module) => ({
		default: module.ContactsPage,
	})),
);
const WaitlistPage = lazy(() =>
	import("@/pages/waitlist/index").then((module) => ({
		default: module.WaitlistPage,
	})),
);
const ActivityPage = lazy(() =>
	import("@/pages/activity/index").then((module) => ({
		default: module.ActivityPage,
	})),
);
const loadDashboardPage = () =>
	import("@/pages/dashboard/index").then((module) => ({
		default: module.DashboardPage,
	}));
const DashboardPage = lazy(loadDashboardPage);

/**
 * React only invokes a lazy component's importer when its parent Suspense
 * boundary reaches that child. On a cold `/dashboard` reload that serialized
 * four sizeable Vite module graphs (providers -> route -> view -> overview),
 * leaving the user on consecutive full-page skeletons even though the backend
 * was healthy. Start the independent imports together as soon as the route
 * matches; React still owns rendering/error boundaries and consumes the same
 * browser module-cache entries when each lazy component mounts.
 */
function preloadDashboardModules(): void {
	Promise.all([
		import("@/app/providers/index"),
		import("@/app/providers/dashboard-progressive-runtime"),
		import("@/pages/dashboard/index").then((module) => {
			// Warm the route's view/tab chunks through the slice's public API so the
			// app layer never reaches into pages/dashboard internals.
			module.preloadDashboardRouteModules();
			return module.DashboardPage;
		}),
	]).catch(() => {
		// Rendering remains the source of truth for surfacing a module failure via
		// the existing Suspense/error boundaries. Preloading is only a head start.
	});
}
const DemoPage = lazy(() =>
	import("@/pages/demo/index").then((module) => ({
		default: module.DemoPage,
	})),
);
const DocsPage = lazy(() =>
	import("@/pages/docs/index").then((module) => ({
		default: module.DocsPage,
	})),
);
const DocumentsPage = lazy(() =>
	import("@/pages/documents/index").then((module) => ({
		default: module.DocumentsPage,
	})),
);
const CalendarPage = lazy(() =>
	import("@/pages/calendar/index").then((module) => ({
		default: module.CalendarPage,
	})),
);
const ChatPage = lazy(() =>
	import("@/pages/chat/index").then((module) => ({
		default: module.ChatPage,
	})),
);
const LandingPage = lazy(() =>
	import("@/pages/landing/index").then((module) => ({
		default: module.LandingPage,
	})),
);
const AlternateLandingPage = lazy(() =>
	import("@/pages/landing/index").then((module) => ({
		default: module.AlternateLandingPage,
	})),
);
const Alternate2LandingPage = lazy(() =>
	import("@/pages/landing/index").then((module) => ({
		default: module.Alternate2LandingPage,
	})),
);
const Alternate3LandingPage = lazy(() =>
	import("@/pages/landing/index").then((module) => ({
		default: module.Alternate3LandingPage,
	})),
);
const Alternate4LandingPage = lazy(() =>
	import("@/pages/landing/index").then((module) => ({
		default: module.Alternate4LandingPage,
	})),
);
const CustomerSupportPage = lazy(() =>
	import("@/pages/landing/index").then((module) => ({
		default: module.CustomerSupportPage,
	})),
);
const SalesPage = lazy(() =>
	import("@/pages/landing/index").then((module) => ({
		default: module.SalesPage,
	})),
);
const LegalPage = lazy(() =>
	import("@/pages/legal/index").then((module) => ({
		default: module.LegalPage,
	})),
);
const OrgOverviewPage = lazy(() =>
	import("@/pages/org-overview/index").then((module) => ({
		default: module.OrgOverviewPage,
	})),
);
const PlaygroundPage = lazy(() =>
	import("@/pages/playground/index").then((module) => ({
		default: module.PlaygroundPage,
	})),
);
const MembersPage = lazy(() =>
	import("@/pages/members/index").then((module) => ({
		default: module.MembersPage,
	})),
);

// --- Helpers ---------------------------------------------------------------

const AUTH_COOKIE_NAME = "better-auth.session_token";

function readSessionCookie(): string | null {
	if (typeof document === "undefined") {
		return null;
	}
	const m = document.cookie.match(
		new RegExp(`(?:^|; )${AUTH_COOKIE_NAME}=([^;]*)`),
	);
	return m ? decodeURIComponent(m[1]) : null;
}

// Session-gated routes are NOT guarded here. The session cookie is httpOnly,
// so a synchronous `beforeLoad` cookie read can never see it — the former
// `requireSession` guard bounced even authenticated users to /login (an
// infinite /dashboard ↔ /login loop). Auth gating is owned entirely by the
// API-based `AuthGate` (mounted in AppProviders), which derives auth state
// from the `/api/auth/get-session` response and wraps every route.

// --- Root ------------------------------------------------------------------

const rootRoute = createRootRoute({
	component: RootLayout,
	notFoundComponent: NotFound,
	errorComponent: RootError,
});

// --- /_auth pathless group --------------------------------------------------

const authLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_auth",
	component: LayoutOutlet,
});

const loginRoute = createRoute({
	getParentRoute: () => authLayoutRoute,
	path: "/login",
	component: LoginPage,
	validateSearch: (search: Record<string, unknown>) => ({
		redirectTo:
			typeof search.redirectTo === "string" ? search.redirectTo : undefined,
	}),
	beforeLoad: ({ search }) => {
		if (isPlausibleSessionToken(readSessionCookie())) {
			const target =
				search.redirectTo && isSafeRedirectTarget(search.redirectTo)
					? search.redirectTo
					: APP_ROUTES.home;
			throw redirect({ to: target });
		}
	},
});

const acceptInvitationRoute = createRoute({
	getParentRoute: () => authLayoutRoute,
	path: "/accept-invitation/$id",
	component: AcceptInvitationRoute,
});

const pendingOrgRoute = createRoute({
	getParentRoute: () => authLayoutRoute,
	path: "/pending-org",
	component: PendingOrgPage,
});

const onboardingCheckoutRoute = createRoute({
	getParentRoute: () => authLayoutRoute,
	path: "/onboarding/checkout",
	component: OnboardingCheckoutPage,
});

const onboardingSuccessRoute = createRoute({
	getParentRoute: () => authLayoutRoute,
	path: "/onboarding/success",
	component: OnboardingSuccessPage,
});

// --- /_core pathless group (session-gated) ----------------------------------

const coreLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_core",
	component: LayoutOutlet,
});

const calendarRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/calendar",
	component: CalendarPage,
});

// `/home` was the calendar's address until the calendar became its own page and
// `/dashboard` became the app's entry. Kept as a permanent redirect rather than
// deleted: it is the target baked into old bookmarks, and into push
// notifications already delivered to devices, which we cannot rewrite.
const homeRedirectRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/home",
	beforeLoad: () => {
		throw redirect({ to: APP_ROUTES.home });
	},
});

const chatRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/chat",
	component: ChatPage,
});

const chatThreadRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/chat/$conversationId",
	component: ChatPage,
});

const dashboardRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/dashboard",
	beforeLoad: preloadDashboardModules,
	component: DashboardPage,
});

const activityRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/activity",
	component: ActivityPage,
});

const billingRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/billing",
	component: BillingPage,
});

const contactsRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/contacts",
	component: ContactsPage,
});

const waitlistRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/waitlist",
	component: WaitlistPage,
});

const campaignsRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/campaigns",
	component: CampaignsPage,
});

const campaignAuthorRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/campaigns/new",
	component: CampaignAuthorPage,
});

const campaignDetailRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/campaigns/$campaignId",
	component: CampaignDetailPage,
});

const membersRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/members",
	component: MembersPage,
});

const orgRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/org",
	component: OrgOverviewPage,
});

const rolesRoute = createRoute({
	getParentRoute: () => coreLayoutRoute,
	path: "/roles",
	component: RolesPage,
});

// --- /_config pathless group (session-gated) -------------------------------

const configLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_config",
	component: LayoutOutlet,
});

const accountRoute = createRoute({
	getParentRoute: () => configLayoutRoute,
	path: "/account",
	component: AccountPage,
});

const configRoute = createRoute({
	getParentRoute: () => configLayoutRoute,
	path: "/config",
	component: ConfigPage,
});

// --- /_admin pathless group (session-gated; role check inside the page) ----

const adminLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_admin",
	component: LayoutOutlet,
});

const superuserRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/superuser",
	component: AdminPage,
});

// --- /_debug pathless group (open) -----------------------------------------

const debugLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_debug",
	component: LayoutOutlet,
});

const playgroundRoute = createRoute({
	getParentRoute: () => debugLayoutRoute,
	path: "/playground",
	component: PlaygroundPage,
});

// --- Top-level routes (no pathless layout) ---------------------------------

// Public landing/marketing page at the root path — rendered for everyone
// (AuthGate lets `/` through as a public route). Authenticated visitors are
// NOT auto-redirected into the app here: a `beforeLoad` can't read the
// httpOnly session cookie, so that redirect would need the session context.
const landingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: LandingPage,
});

// Landing redesign DRAFT — same public treatment as `/`. Lives at /alternate
// so the redesign can be reviewed side-by-side without touching the current
// landing; delete (or promote to `/`) once the direction is decided.
const alternateLandingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/alternate",
	component: AlternateLandingPage,
});

// Redesign drafts #2-#4 — independent design explorations, same draft
// treatment as /alternate.
const alternate2LandingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/alternate2",
	component: Alternate2LandingPage,
});

const alternate3LandingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/alternate3",
	component: Alternate3LandingPage,
});

const alternate4LandingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/alternate4",
	component: Alternate4LandingPage,
});

// Public use-case / solutions marketing pages — same public treatment as `/`.
const supportUseCaseRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/solutions/customer-support",
	component: CustomerSupportPage,
});

const salesUseCaseRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/solutions/sales",
	component: SalesPage,
});

const demoRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/demo",
	component: DemoPage,
});

const documentsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/documents",
	component: DocumentsPage,
});

// Catch-all for /docs/* — DocsPage reads params via this route.
const docsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/docs/$",
	component: DocsPage,
});

// Catch-all for /legal/* (terms, privacy, dpa, subprocessors). Public route —
// LegalPage reads the slug from `_splat`; empty splat renders the legal index.
const legalRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/legal/$",
	component: LegalPage,
});

// --- Tree ------------------------------------------------------------------

const routeTree = rootRoute.addChildren([
	authLayoutRoute.addChildren([
		loginRoute,
		acceptInvitationRoute,
		pendingOrgRoute,
		onboardingCheckoutRoute,
		onboardingSuccessRoute,
	] as readonly AnyRoute[]),
	coreLayoutRoute.addChildren([
		calendarRoute,
		homeRedirectRoute,
		chatRoute,
		chatThreadRoute,
		dashboardRoute,
		activityRoute,
		billingRoute,
		contactsRoute,
		waitlistRoute,
		campaignsRoute,
		campaignAuthorRoute,
		campaignDetailRoute,
		membersRoute,
		orgRoute,
		rolesRoute,
	] as readonly AnyRoute[]),
	configLayoutRoute.addChildren([
		accountRoute,
		configRoute,
	] as readonly AnyRoute[]),
	adminLayoutRoute.addChildren([superuserRoute] as readonly AnyRoute[]),
	debugLayoutRoute.addChildren([playgroundRoute] as readonly AnyRoute[]),
	landingRoute,
	supportUseCaseRoute,
	salesUseCaseRoute,
	alternateLandingRoute,
	alternate2LandingRoute,
	alternate3LandingRoute,
	alternate4LandingRoute,
	demoRoute,
	documentsRoute,
	docsRoute,
	legalRoute,
]);

// Restore scroll positions only when the *pathname* changes (real page
// navigations, incl. back/forward). Same-path location changes are in-page
// UI state written to the URL (nuqs `?section=` / `?tab=` / drawer params):
// TanStack tracks every scrolled element — including the app's custom
// ThemedScrollbar scroller — and restoring it on those writes issued an
// instant `scrollTop` snap-back that cancelled in-flight smooth scrolls
// (the /config and /account section-nav clicks).
let lastRestoredPathname: string | null = null;

export const router = createRouter({
	routeTree,
	defaultPreload: "intent",
	defaultPreloadStaleTime: 0,
	scrollRestoration: ({ location }) => {
		const pathnameChanged = lastRestoredPathname !== location.pathname;
		lastRestoredPathname = location.pathname;
		return pathnameChanged;
	},
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
